import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query'
import {
  decisionsApi,
  bridgeApi,
  type DecisionStatus,
  type DecisionListResponse,
} from '@/lib/api'
import { toast } from '@/lib/toast'

/**
 * List decisions for a given lifecycle status (Pending / Approved / Rejected).
 * Keyed by status so each tab caches independently; `keepPreviousData` keeps the
 * previous tab's rows + counts on screen during a switch so the count headers
 * never flash empty. Defaults to PENDING — the dashboard widgets that call
 * `useDecisions()` with no argument only ever want the pending queue.
 */
export function useDecisions(status: DecisionStatus = 'PENDING') {
  return useQuery({
    queryKey: ['decisions', status],
    queryFn: () => decisionsApi.list(status),
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  })
}

// ─── Optimistic count shifting ─────────────────────────────────────────────────
// The instant Mark acts, shift the tab-header counts across every cached
// decisions query so the headers update before the API responds. The row itself
// is removed by the onSettled refetch (and the exit animation in the page covers
// the gap), so we only patch counts here.

interface CountDelta {
  pending?: number
  approved?: number
  rejected?: number
}

function patchCounts(queryClient: QueryClient, delta: CountDelta) {
  queryClient.setQueriesData<DecisionListResponse>({ queryKey: ['decisions'] }, (old) => {
    if (!old?.counts) return old
    return {
      ...old,
      counts: {
        pending: Math.max(0, old.counts.pending + (delta.pending ?? 0)),
        approved: Math.max(0, old.counts.approved + (delta.approved ?? 0)),
        rejected: Math.max(0, old.counts.rejected + (delta.rejected ?? 0)),
      },
    }
  })
}

function snapshotCounts(queryClient: QueryClient) {
  return queryClient.getQueriesData<DecisionListResponse>({ queryKey: ['decisions'] })
}

function restoreCounts(
  queryClient: QueryClient,
  previous: ReturnType<typeof snapshotCounts> | undefined,
) {
  previous?.forEach(([key, data]) => queryClient.setQueryData(key, data))
}

function invalidateDecisionViews(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ['decisions'] })
  void queryClient.invalidateQueries({ queryKey: ['stats'] })
  void queryClient.invalidateQueries({ queryKey: ['agents'] })
}

// ─── Single-decision actions ──────────────────────────────────────────────────
// Each action resolves in the Athena runtime first, then best-effort notifies
// the TMAgent product API via the bridge (failures there are swallowed).

async function approveOne(id: string) {
  const result = await decisionsApi.approve(id)
  await bridgeApi.approve(id, 'mark').catch(() => {})
  return result
}

async function rejectOne(id: string, reason?: string) {
  const result = await decisionsApi.reject(id, reason)
  await bridgeApi.reject(id, 'mark', reason ?? 'No reason provided').catch(() => {})
  return result
}

export function useApproveDecision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => approveOne(id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['decisions'] })
      const previous = snapshotCounts(queryClient)
      patchCounts(queryClient, { pending: -1, approved: +1 })
      return { previous }
    },
    onError: (_err, _id, ctx) => {
      restoreCounts(queryClient, ctx?.previous)
      toast('Could not approve — it may have already been actioned.', 'error')
    },
    onSuccess: () => toast('Decision approved — the agent is cleared to proceed.', 'success'),
    onSettled: () => invalidateDecisionViews(queryClient),
  })
}

export function useRejectDecision() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectOne(id, reason),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['decisions'] })
      const previous = snapshotCounts(queryClient)
      patchCounts(queryClient, { pending: -1, rejected: +1 })
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      restoreCounts(queryClient, ctx?.previous)
      toast('Could not reject — it may have already been actioned.', 'error')
    },
    onSuccess: () => toast('Decision rejected — the agent has been told to hold.', 'success'),
    onSettled: () => invalidateDecisionViews(queryClient),
  })
}

// ─── Batch actions ────────────────────────────────────────────────────────────
// Used by "approve all safe" (dashboard) and the per-type batch buttons in the
// decision queue. Each id is actioned independently; the result reports how many
// succeeded so the UI can surface partial failures.

export interface BatchResult {
  total: number
  succeeded: number
  failed: number
}

function summarise(results: PromiseSettledResult<unknown>[], total: number): BatchResult {
  const failed = results.filter((r) => r.status === 'rejected').length
  return { total, succeeded: total - failed, failed }
}

function toastBatch(verb: 'Approved' | 'Rejected', r: BatchResult) {
  if (r.failed === 0) {
    toast(`${verb} ${r.succeeded} decision${r.succeeded === 1 ? '' : 's'}.`, 'success')
  } else {
    toast(`${verb} ${r.succeeded} of ${r.total} — ${r.failed} left pending.`, 'error')
  }
}

export function useApproveDecisions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]): Promise<BatchResult> => {
      const results = await Promise.allSettled(ids.map((id) => approveOne(id)))
      return summarise(results, ids.length)
    },
    onSuccess: (r) => toastBatch('Approved', r),
    onSettled: () => invalidateDecisionViews(queryClient),
  })
}

export function useRejectDecisions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ ids, reason }: { ids: string[]; reason?: string }): Promise<BatchResult> => {
      const results = await Promise.allSettled(ids.map((id) => rejectOne(id, reason)))
      return summarise(results, ids.length)
    },
    onSuccess: (r) => toastBatch('Rejected', r),
    onSettled: () => invalidateDecisionViews(queryClient),
  })
}
